/**
 * ProductCategoryListPresenter
 * --
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  Text,
  InputFormTable,
  Information,
  Pagination,
  ModalLayout,
} from 'components';
import {
  Table,
  Button,
  Radio,
  DatePicker,
  Input,
  Select,
  Tree,
  Spin,
  TreeSelect,
  Splitter,
  Flex,
  Tag,
  Image,
  Space,
} from 'antd';
import { CloseOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { STORE_URL, stringToMoneyFormat } from 'utils';
import ProductDetail from 'routes/admin.routes/admin.products/ProductDetail';
import { useUserStack } from 'store';

const SORT_OPTIONS = [
  {
    label: '최근 등록상품이 위로',
    value: 'created_at-DESC',
    type: 'number',
  },
  {
    label: '최근 등록상품이 아래로',
    value: 'created_at-ASC',
    type: 'number',
  },
  {
    label: '최근 수정상품이 위로',
    value: 'updated_at-DESC',
    type: 'number',
  },
  {
    label: '최근 수정상품이 아래로',
    value: 'updated-ASC',
    type: 'number',
  },
  {
    label: '판매가 높은 상품이 위로',
    value: 'price-DESC',
    type: 'number',
  },
  {
    label: '판매가 높은 상품이 아래로',
    value: 'price-ASC',
    type: 'number',
  },
  {
    label: '상품명 가나다순',
    value: 'product_name-ASC',
    type: 'string',
  },
  {
    label: '상품명 가나다역순',
    value: 'product_name-DESC',
    type: 'string',
  },
];

const SOLD_OPTIONS = [
  {
    label: '품절상품 상관없음',
    value: 1,
  },
  {
    label: '품절상품 맨 뒤로',
    value: 0,
  },
];

const DELIVERY_TYPE = {
  DELIVERY: '일반',
  SUBSCRIPTION: '정기배송',
  RENTAL: '렌탈',
};

const PRODUCT_TYPE = {
  GENERAL: '일반',
  REFRIGERATION: '냉장',
  FREEZING: '냉동',
};

const PRODUCT_STATUS = {
  NORMAL: {
    title: '정상',
    color: 'green',
  },
  SOLDOUT: {
    title: '품절',
    color: 'default',
  },
  END: {
    title: '종료',
    color: 'danger',
  },
  STOP: {
    title: '중지',
    color: 'warning',
  },
  WAIT: {
    title: '대기',
    color: 'processing',
  },
};

/**
 *
 * --
 */
const ProductCategoryListPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    isOpened,
    modalData,
    modalType,
    onOpen,
    onChange,
    searchValue,
    onSearch,
    onSubmit,
    onDelete,
    onUpdateOrder,

    data,
    categories,
    isLoading,
  } = props;
  /* ===== STATE ===== */
  const userInfo = useUserStack();

  /* ===== STATE ===== */
  const fileRef = React.useRef(null);
  const ITEMS_PER_PAGE = 10; // 페이지 당 항목 수
  const [selectedItem, setSelectedItem] = useState(null);
  const [tabKey, setTabKey] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  // const [productList, setProductList] = useState([]);
  const [modalSelectItem, setModalSelectItem] = useState(null);
  const [modalChildItem, setModalChildItem] = useState([]);
  const [isChange, setIsChange] = useState(false);
  const [productModal, setProductModal] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState(null);

  const [sortValue, setSortValue] = useState({
    sort: null,
    sold: 1,
  });

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  const formItem = [
    {
      label: '검색어',
      direction: 'column',
      form: [
        {
          input: (
            <>
              <Select
                size="large"
                value={'상품명'}
                options={[
                  { label: '상품명', value: '상품명' },
                  { label: '회원명', value: '회원명' },
                  { label: '전화번호', value: '전화번호' },
                ]}
                style={{ ...styles.form.input.large }}
              />
              <Input
                size="large"
                placeholder="검색어를 입력해주세요"
                style={{ ...styles.form.input.large, width: 350 }}
              />
              <Button size="large" icon={<PlusOutlined />} />
            </>
          ),
        },
      ],
    },
    {
      label: '상품 구분',
      form: [
        {
          input: (
            <>
              <Radio.Group value={'전체'}>
                <Radio value={'전체'}>전체</Radio>
                <Radio value={'기본상품'}>기본상품</Radio>
                <Radio value={'세트상품'}>세트상품</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    {
      label: '상품 분류',
      form: [
        {
          input: (
            <>
              <Select
                size="large"
                options={[
                  { label: '대분류1', value: '대분류1' },
                  { label: '대분류2', value: '대분류2' },
                  { label: '대분류3', value: '대분류3' },
                ]}
                style={{ ...styles.form.input.large }}
                placeholder={'대분류 선택'}
              />
              <Select
                size="large"
                options={[
                  { label: '중분류1', value: '중분류1' },
                  { label: '중분류2', value: '중분류2' },
                  { label: '중분류3', value: '중분류3' },
                ]}
                style={{ ...styles.form.input.large }}
                placeholder={'중분류 선택'}
              />
              <Select
                size="large"
                options={[
                  { label: '소분류1', value: '소분류1' },
                  { label: '소분류2', value: '소분류2' },
                  { label: '소분류3', value: '소분류3' },
                ]}
                style={{ ...styles.form.input.large }}
                placeholder={'소분류 선택'}
              />
              <Select
                size="large"
                options={[
                  { label: '상세분류1', value: '상세분류1' },
                  { label: '상세분류2', value: '상세분류2' },
                  { label: '상세분류3', value: '상세분류3' },
                ]}
                style={{ ...styles.form.input.large }}
                placeholder={'상세분류 선택'}
              />
            </>
          ),
        },
      ],
    },
    {
      label: '상품 등록일',
      form: [
        {
          input: (
            <Button size="large" style={{ ...styles.form.button }}>
              오늘
            </Button>
          ),
        },
        {
          input: (
            <Button size="large" style={{ ...styles.form.button }}>
              1주일
            </Button>
          ),
        },
        {
          input: (
            <Button size="large" style={{ ...styles.form.button }}>
              1개월
            </Button>
          ),
        },
        {
          input: (
            <Button size="large" style={{ ...styles.form.button }}>
              3개월
            </Button>
          ),
        },
        {
          input: (
            <Button size="large" style={{ ...styles.form.button }}>
              전체
            </Button>
          ),
        },
        {
          input: (
            <>
              <DatePicker
                size="large"
                format={'YYYY-MM-DD'}
                placeholder=""
                style={{ ...styles.form.date }}
              />
              <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
              <DatePicker
                size="large"
                format={'YYYY-MM-DD'}
                placeholder=""
                style={{ ...styles.form.date }}
              />
            </>
          ),
        },
      ],
    },
    {
      label: '진열 상태',
      form: [
        {
          input: (
            <>
              <Radio.Group value={'전체'}>
                <Radio value={'전체'}>전체</Radio>
                <Radio value={'진열함'}>진열함</Radio>
                <Radio value={'진열안함'}>진열안함</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    {
      label: '판매 상태',
      form: [
        {
          input: (
            <>
              <Radio.Group value={'전체'}>
                <Radio value={'전체'}>전체</Radio>
                <Radio value={'판매함'}>판매함</Radio>
                <Radio value={'판매안함'}>판매안함</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const form = [
    {
      label: '카테고리 번호',
      form: [
        {
          input: <p>{selectedItem?.product_category_id}</p>,
        },
      ],
    },
    {
      label: '카테고리 명',
      form: [
        {
          input: (
            <>
              <Input
                count={{
                  show: true,
                  max: 20,
                }}
                placeholder="카테고리 명을 입력하세요."
                style={{ ...styles.form.input.large }}
                value={selectedItem?.product_category_name}
                onChange={(e) =>
                  setSelectedItem({
                    ...selectedItem,
                    product_category_name: e.target.value,
                  })
                }
              />
            </>
          ),
        },
      ],
    },
    {
      label: '표시여부',
      form: [
        {
          input: (
            <>
              <Radio.Group
                onChange={(e) =>
                  setSelectedItem({
                    ...selectedItem,
                    is_show: e.target.value,
                  })
                }
                value={selectedItem?.is_show}
              >
                <Radio value={'Y'}>노출</Radio>
                <Radio value={'N'}>노출안함</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    ...(selectedItem?.shop_information_id !== 1
      ? [
          {
            label: '승인여부',
            form: [
              {
                input: (
                  <>
                    <Radio.Group
                      onChange={(e) =>
                        setSelectedItem({
                          ...selectedItem,
                          application_status: e.target.value,
                        })
                      }
                      value={selectedItem?.application_status}
                    >
                      <Radio value={'APPROVE'}>승인</Radio>
                      <Radio value={'APPLY'}>승인신청</Radio>
                      <Radio value={'REJECT'}>반려</Radio>
                    </Radio.Group>
                  </>
                ),
              },
            ],
          },
        ]
      : []),
    {
      label: '아이콘',
      form: [
        {
          input: (
            <>
              {!selectedItem?.icon_url ? (
                <>
                  <Button onClick={() => fileRef.current.click()}>
                    업로드
                  </Button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) =>
                      setSelectedItem({
                        ...selectedItem,
                        icon_url: e.target.files[0],
                      })
                    }
                  />
                </>
              ) : null}

              {selectedItem?.icon_url ? (
                <div style={{ position: 'relative', top: 0, left: 0 }}>
                  <Image
                    src={
                      typeof selectedItem?.icon_url === 'string'
                        ? `${STORE_URL}/${selectedItem?.icon_url}`
                        : URL.createObjectURL(selectedItem?.icon_url)
                    }
                    width={50}
                    height={50}
                  />

                  <Button
                    size="small"
                    danger
                    icon={<CloseOutlined />}
                    style={{ position: 'absolute', top: -10, right: -10 }}
                    onClick={() =>
                      setSelectedItem((prev) => ({
                        ...prev,
                        icon_url: null,
                      }))
                    }
                  />
                </div>
              ) : null}
            </>
          ),
        },
      ],
    },
  ];

  /* ===== FUNCTION ===== */
  const tabletSelectData = (category) => {
    const tablet = (children) => {
      return children.map((child) => ({
        title: child.product_category_name,
        value: child.product_category_id,
        children: child.children ? tablet(child.children) : null,
      }));
    };

    return tablet(category);
  };

  const orderChange = (child, newOrder) => {
    const updatedList = modalChildItem.map((item) => {
      if (item.order === child.order) {
        // 현재 아이템의 순서를 변경
        return { ...item, order: newOrder };
      } else if (item.order >= newOrder && item.order < child.order) {
        // 기존 아이템 중 `newOrder` 이상이면서, 현재 순서보다 낮은 아이템은 순서 +1
        return { ...item, order: item.order + 1 };
      } else if (item.order > child.order && item.order <= newOrder) {
        // 기존 아이템 중 현재 순서보다 높으면서 `newOrder` 이하인 아이템은 순서 -1
        return { ...item, order: item.order - 1 };
      }
      return item; // 나머지는 변경하지 않음
    });

    // 순서로 정렬
    const sortedList = updatedList.sort((a, b) => a.order - b.order);

    setModalChildItem(sortedList);
    setIsChange(true);
  };

  /* ===== HOOKS ===== */
  /**
   * 선택한 카테고리에 따른 상품 정제
   * --
   */
  const productList = useMemo(() => {
    if (!selectedItem) return;

    const { product_category_id } = selectedItem;

    const findProducts = data.filter((product) => {
      const productSplitKey = product?.categoryKey?.split('-');

      return productSplitKey?.some(
        (key) => Number(key) === product_category_id
      );
    });

    const { sort, sold } = sortValue;

    let sortData = findProducts;

    if (sort) {
      const split = sort.split('-');

      const findType = SORT_OPTIONS.find((item) => item.value === sort).type;
      switch (split[1]) {
        case 'DESC':
          sortData = sortData.sort((a, b) => {
            if (findType === 'string') {
              return b[split[0]]?.localeCompare(a[split[0]], 'ko', {
                numeric: true,
                sensitivity: 'base',
              });
            } else {
              return b[split[0]] - a[split[0]];
            }
          });
          break;
        case 'ASC':
          sortData = sortData.sort((a, b) => {
            if (findType === 'string') {
              return a[split[0]]?.localeCompare(b[split[0]], 'ko', {
                numeric: true,
                sensitivity: 'base',
              });
            } else {
              return a[split[0]] - b[split[0]];
            }
          });
          break;
        default:
          break;
      }
    }

    if (!sold) {
      sortData = sortData.sort((a, b) => {
        // 품절 상품을 맨 뒤로 보내기
        const aOutOfStock = a.num === 0 || a.product_status === 'SOLDOUT';
        const bOutOfStock = b.num === 0 || b.product_status === 'SOLDOUT';

        if (aOutOfStock && !bOutOfStock) return 1; // a가 품절이면 뒤로 이동
        if (!aOutOfStock && bOutOfStock) return -1; // b가 품절이면 a를 앞으로 이동

        return 0; // 기존 순서 유지
      });
    }

    if (searchKeyword) {
      sortData = sortData?.filter((product) => {
        return product?.product_name?.includes(searchKeyword);
      });
    }
    return sortData;
  }, [selectedItem, data, sortValue, searchKeyword]);

  /**
   * 모달 순서변경 선택값에 따른 자식 노드 정제
   * --
   */

  useEffect(() => {
    if (modalSelectItem) {
      setModalChildItem(modalSelectItem.children);
    } else {
      setModalChildItem(categories);
    }
    // eslint-disable-next-line
  }, [modalSelectItem]);

  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row>
            <Col x={24}>
              <Title style={{ ...styles.title }}>상품 카테고리 관리</Title>
            </Col>

            <Col x={4}>
              <Content
                maxWidth={'100%'}
                padding={5}
                backgroundColor="none"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid #70737C',
                  borderColor: 'rgba(112, 115, 124, 0.2)',
                }}
              >
                <Button
                  size="small"
                  style={{
                    borderColor: 'var(--primary-color)',
                    color: 'var(--primary-color)',
                  }}
                  onClick={() => onOpen(isOpened, 'category')}
                >
                  카테고리 추가 +
                </Button>
                <Button
                  size="small"
                  color="default"
                  onClick={() => onOpen(isOpened, 'order')}
                >
                  카테고리 순서/편집
                </Button>
              </Content>
              <Content maxWidth={'100%'} backgroundColor="none">
                <Tree
                  showLine
                  treeData={categories}
                  onSelect={(_, { node }) => {
                    setSortValue({
                      sort: null,
                      sold: 1,
                    });
                    setCurrentPage(1);
                    setSearchKeyword(null);
                    setSelectedItem(node);
                  }}
                  onDeSelect={(key, item) => setSelectedItem(null)}
                  rootStyle={{ background: 'none' }}
                />
              </Content>
            </Col>
            <Col x={20}>
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor="none"
                style={{
                  borderLeft: '1px solid #70737C',
                  borderColor: 'rgba(112, 115, 124, 0.2)',
                }}
              >
                {selectedItem ? (
                  <Content maxWidth={'100%'} style={{ marginBottom: 40 }}>
                    <ul
                      style={{
                        display: 'flex',
                        alignItems: 'stretch',
                        width: '100%',
                        marginBottom: 10,
                      }}
                    >
                      {[{ label: '카테고리 설정', key: 1 }]?.map(
                        (_item, _index) => (
                          <li
                            key={`tab_${_index}`}
                            onClick={() => setTabKey(_item.key)}
                            style={{
                              border: '1px solid #000',
                              borderColor:
                                tabKey === _item.key ? '#000' : '#B6AEAE',
                              borderBottom:
                                tabKey === _item.key
                                  ? 'none'
                                  : '1px solid #000',
                              fontSize: 14,
                              fontWeight: tabKey === _item.key ? 600 : 400,
                              color: tabKey === _item.key ? '#000' : '#B6AEAE',
                              padding: '10px 20px',
                              cursor: 'pointer',
                              // display:
                              //   isCreating && _item.key === 2
                              //     ? 'none'
                              //     : 'inline-block',
                            }}
                          >
                            {_item.label}
                          </li>
                        )
                      )}
                      <li
                        style={{
                          flex: 1,
                          borderBottom: '1px solid var(--black-color)',
                        }}
                      ></li>
                    </ul>
                    {tabKey === 1 ? (
                      <>
                        {/* 카테고리 설정 */}
                        <Content
                          maxWidth={'100%'}
                          padding={0}
                          backgroundColor={'none'}
                        >
                          <Information
                            content={[
                              '탭별 카테고리 설정은 표시여부에 따라 메인 화면에 반영됩니다.',
                              '카테고리 설정은 최종적으로 [저장] 버튼을 클릭 후 반영됩니다.',
                              '카테고리 번호는 저장 후에 생성 됩니다.',
                              '카테고리에 맵핑 되는 상품은 추가/수정 가능합니다.',
                            ]}
                          />
                          <div style={{ marginTop: 10 }}>
                            <InputFormTable form={form} />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <Button
                              size="large"
                              type="primary"
                              style={{
                                background: 'var(--primary-color)',
                                width: 110,
                                marginTop: 40,
                                marginRight: 10,
                              }}
                              onClick={() =>
                                onSubmit('category', 'UPDATE', selectedItem)
                              }
                            >
                              {'변경사항 저장'}
                            </Button>
                            <Button
                              size="large"
                              style={{
                                width: 110,
                                marginTop: 40,
                              }}
                              onClick={() =>
                                onDelete(selectedItem?.product_category_id)
                              }
                            >
                              {'삭제'}
                            </Button>
                          </div>
                        </Content>
                        {/* 카테고리 설정 끝 */}
                      </>
                    ) : (
                      <>
                        {/* 카테고리 상품관리 */}
                        <Content
                          maxWidth={'100%'}
                          padding={0}
                          backgroundColor={'none'}
                        >
                          <ExpandableContainer borderRadius={4} padding={20}>
                            {formItem?.map((_item, _index) => (
                              <div
                                key={`form_${_index}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '10px 0px',
                                }}
                              >
                                <div
                                  style={{
                                    width: 120,
                                    fontSize: 13,
                                    fontWeight: 400,
                                  }}
                                >
                                  <label>{_item.label}</label>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  {_item.form?.map((_subitem, _subkey) => (
                                    <React.Fragment key={`input_${_subkey}`}>
                                      {_subitem.input}
                                    </React.Fragment>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </ExpandableContainer>

                          {/* 검색/초기화 버튼 */}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: '100%',
                              marginTop: 48,
                            }}
                          >
                            <Button
                              size="large"
                              type="primary"
                              style={{
                                ...styles.button,
                                background: 'var(--primary-color)',
                              }}
                            >
                              검색
                            </Button>
                            <Button size="large" style={{ ...styles.button }}>
                              초기화
                            </Button>
                          </div>
                          {/* 검색/초기화 버튼 끝 */}
                        </Content>
                        {/* 카테고리 상품관리 끝 */}
                      </>
                    )}
                  </Content>
                ) : (
                  <div
                    style={{
                      height: 300,
                      marginBottom: 40,
                      background: '#FFFFFF',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <p style={{ fontSize: 25 }}>카테고리를 선택해주세요</p>
                  </div>
                )}

                {/* 검색결과 */}
                <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{ marginBottom: 12 }}
                  >
                    <h4 style={{ fontSize: 14, fontWeight: 500 }}>
                      검색 결과 총 <span>{productList?.length || 0}</span>건
                    </h4>

                    <Flex align="center" gap={8}>
                      <Radio.Group
                        value={sortValue.sold}
                        options={SOLD_OPTIONS}
                        onChange={(e) =>
                          setSortValue((prev) => ({
                            ...prev,
                            sold: e.target.value,
                          }))
                        }
                        optionType="button"
                        buttonStyle="solid"
                      />

                      <Select
                        value={sortValue.sort}
                        options={SORT_OPTIONS}
                        placeholder={'정렬옵션'}
                        onChange={(e) =>
                          setSortValue((prev) => ({
                            ...prev,
                            sort: e,
                          }))
                        }
                        style={{ width: 180 }}
                      />
                    </Flex>
                  </Flex>
                  <Table
                    size="small"
                    dataSource={productList?.slice(
                      (currentPage - 1) * ITEMS_PER_PAGE,
                      currentPage * ITEMS_PER_PAGE
                    )}
                    pagination={false}
                    columns={[
                      {
                        title: '상품번호',
                        key: 'product_id',
                        dataIndex: 'product_id',
                        width: 100,
                      },
                      {
                        title: '상품명',
                        key: 'product_name',
                        dataIndex: 'product_name',
                        render: (value, row) => (
                          <a onClick={() => setProductModal(row)}>{value}</a>
                        ),
                        filterDropdown: ({
                          selectedKeys,
                          confirm,
                          clearFilters,
                        }) => (
                          <div style={{ padding: 8 }}>
                            <Input
                              value={searchKeyword}
                              onChange={(e) => setSearchKeyword(e.target.value)}
                              onPressEnter={() => confirm()}
                              placeholder={`상품명 검색`}
                            />
                            <Space style={{ marginTop: 8 }}>
                              <Button
                                type="primary"
                                onClick={() => confirm()}
                                icon={<SearchOutlined />}
                                size="small"
                                style={{ width: 90 }}
                              >
                                검색
                              </Button>
                              <Button
                                onClick={() => {
                                  setSearchKeyword(null);
                                  clearFilters();
                                }}
                                size="small"
                                style={{ width: 90 }}
                              >
                                초기화
                              </Button>
                            </Space>
                          </div>
                        ),
                      },
                      {
                        title: '업체',
                        key: 'brand',
                        dataIndex: 'brand',
                      },
                      {
                        title: '카테고리',
                        key: 'category',
                        dataIndex: 'category',
                      },
                      {
                        title: '판매가',
                        key: 'price',
                        dataIndex: 'price',
                        width: 90,
                        render: (item) => stringToMoneyFormat(item, '원'),
                      },
                      {
                        title: '수량',
                        key: 'num',
                        dataIndex: 'num',
                        width: 90,
                        render: (item) => stringToMoneyFormat(item, '개'),
                      },
                      {
                        title: '포장 타입',
                        key: 'packaging_method',
                        dataIndex: 'packaging_method',
                        width: 90,
                        render: (item) => PRODUCT_TYPE[item],
                      },
                      {
                        title: '배송 타입',
                        key: 'type',
                        dataIndex: 'type',
                        width: 90,
                        render: (item) => DELIVERY_TYPE[item],
                      },
                      {
                        title: '판매상태',
                        key: 'product_status',
                        dataIndex: 'product_status',
                        width: 90,
                        render: (item) => (
                          <Tag color={PRODUCT_STATUS[item]?.color}>
                            {PRODUCT_STATUS[item]?.title}
                          </Tag>
                        ),
                      },
                    ]}
                    scroll={{ x: 1400 }}
                  />
                  <Pagination
                    current={currentPage}
                    total={productList?.length || 0}
                    pageSize={ITEMS_PER_PAGE}
                    onChange={handlePageChange}
                    style={{ marginTop: 40 }}
                  />
                </Content>
                {/* 검색결과 끝 */}
              </Content>
            </Col>
          </Row>
        </Content>
      </Spin>

      {/* 카테고리 추가 */}
      <ModalLayout
        type="modal"
        centered
        title={'카테고리 추가'}
        open={isOpened && modalType === 'category'}
        onCancel={() => onOpen(isOpened)}
        closable
        width={500}
        footer={
          <Content
            maxWidth={'100%'}
            padding={0}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button onClick={() => onOpen(isOpened)}>닫기</Button>
            <Button
              type="primary"
              onClick={() =>
                onSubmit(
                  'category',
                  modalData?.category?.product_category_id ? 'UPDATE' : 'CREATE'
                )
              }
            >
              저장
            </Button>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={'30px 0px'}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <div style={{ marginBottom: '20px' }}>
                  <TreeSelect
                    size="large"
                    treeData={tabletSelectData(categories) || []}
                    style={{ width: '100%' }}
                    placeholder="상위 카테고리 선택 (선택사항)"
                    value={modalData?.category?.parents_id}
                    onChange={(value) =>
                      onChange('parents_id', value, 'category')
                    }
                  />
                </div>

                <div>
                  <Input
                    size="large"
                    count={{
                      show: true,
                      max: 20,
                    }}
                    placeholder="카테고리를 입력해주세요"
                    value={modalData?.category?.product_category_name}
                    onChange={(e) =>
                      onChange(
                        'product_category_name',
                        e.target.value,
                        'category'
                      )
                    }
                  />
                </div>
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* 순서 편집 */}
      <ModalLayout
        type="modal"
        centered
        title={'메뉴 순서 편집'}
        open={isOpened && modalType === 'order'}
        onCancel={() => onOpen(isOpened)}
        closable
        width={1000}
        height={'50%'}
      >
        <Content maxWidth={'100%'} padding={'30px 0px'}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                {/* 메뉴 드래그 앤 드롭 */}
                <Splitter>
                  <Splitter.Panel defaultSize={'60%'} min={'30%'} max={'70%'}>
                    <Tree
                      showLine
                      treeData={categories}
                      onSelect={(_, { node }) => {
                        if (
                          modalSelectItem?.product_category_id ===
                          node?.product_category_id
                        ) {
                          setModalSelectItem(null);
                        } else {
                          setModalSelectItem(node);
                        }
                        setIsChange(false);
                      }}
                    />
                  </Splitter.Panel>
                  <Splitter.Panel defaultSize={'40%'} min={'30%'} max={'70%'}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        padding: '0px 12px',
                      }}
                    >
                      {modalChildItem?.map((child, _index) => (
                        <div
                          key={`child-${child.product_category_id}`}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: '1px solid #EEEEEE',
                            padding: '12px',
                            borderRadius: 4,
                          }}
                        >
                          <Text>{child.product_category_name}</Text>
                          <Select
                            value={_index + 1}
                            options={new Array(modalChildItem.length)
                              .fill(null)
                              .map((_, i) => ({
                                label: i + 1,
                                value: i + 1,
                              }))}
                            onChange={(e) => orderChange(child, e)}
                          />
                        </div>
                      ))}

                      {isChange ? (
                        <Content
                          maxWidth={'100%'}
                          padding={0}
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 10,
                            marginTop: 30,
                          }}
                        >
                          <Button
                            onClick={() => {
                              onOpen(isOpened);
                              setIsChange(false);
                            }}
                          >
                            취소
                          </Button>
                          <Button
                            type="primary"
                            onClick={() => onUpdateOrder(modalChildItem)}
                          >
                            저장
                          </Button>
                        </Content>
                      ) : null}
                    </div>
                  </Splitter.Panel>
                </Splitter>
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* 상품상세보기 */}
      <ModalLayout
        // type="drawer"
        centered
        // placement="bottom"
        title={'상품상세보기'}
        open={productModal !== null}
        onCancel={() => setProductModal(null)}
        closable
        width={'95%'}
        height={'95%'}
      >
        {productModal && (
          <ProductDetail
            permission={userInfo.type}
            defaultShopId={userInfo.shop_information_id}
            defaultProductId={productModal.product_id}
          />
        )}
      </ModalLayout>
    </>
  );
};

export default ProductCategoryListPresenter;
